import Image from "next/image";
import { Swiper, SwiperSlide } from 'swiper/react';
import Container from "../Container";
import { useEffect, useRef, useState, useMemo } from "react";
import SvgArrowLeft from "../../svg/SvgArrowLeft";
import SvgArrowRight from "../../svg/SvgArrowRight";
import FeaturesGrid from "../FeaturesGrid";

const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        const listener = () => setMatches(media.matches);

        listener(); // Initialize
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
    }, [query]);

    return matches;
};

export default function SectionFeatures({ items, icons, theme = 'white-on-black', className = '' }) {
    const showCarousel = useMediaQuery('(max-width:767px)');
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const processedItems = items.map((item, key) => ({
        ...item,
        icon: icons[key],
    }));

    const carousel = useMemo(() => {
        return (
            <Swiper
                className="w-full"
                navigation={{
                    prevEl: navigationPrevRef.current,
                    nextEl: navigationNextRef.current,
                }}
                loop={true}
                spaceBetween={0}
                slidesPerView={1}
                onBeforeInit={(swiper) => {
                    swiper.params.navigation.prevEl = navigationPrevRef.current;
                    swiper.params.navigation.nextEl = navigationNextRef.current;
                }}
            >
                {processedItems.map((item, key) => {
                    return (
                        <SwiperSlide key={`Feature: ${key}`}>
                            <div className="flex flex-col gap-6 items-center">
                                <div className="flex">
                                    <Image src={item.icon}
                                        width={100}
                                        height={100}
                                        alt={`${item.title} icon`}
                                    />
                                </div>
                                <h3 className="text-xl">{item.title}</h3>
                                <p className={`text-sm ${theme === 'white-on-black' ? 'text-white' : 'text-black'} max-w-xs leading-relaxed`}
                                    dangerouslySetInnerHTML={{ __html: item.body }} />
                            </div>
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        )
    }, [processedItems, theme]);

    return (
        <section
            className={`${theme === 'white-on-black' ? 'bg-black text-white' : 'bg-white text-black'} w-full text-center ${className}`}>
            <Container className="relative" fullMobile={true}>
                {showCarousel && carousel}

                {!showCarousel && (
                    <FeaturesGrid
                        bodyColor={theme === 'white-on-black' ? 'white' : 'black'}
                        items={processedItems}
                    />
                )}

                <div
                    className="inline-flex md:hidden relative mt-6 mx-auto divided top-12 z-20 items-stretch rounded-lg overflow-hidden shadow-xl">
                    <button ref={navigationPrevRef}
                        className="py-9 px-10 bg-white text-black mouse-hover:bg-black mouse-hover:text-white transition-colors will-change-transform duration-300">
                        <SvgArrowLeft width={25} id="section-features" />
                    </button>
                    <button ref={navigationNextRef}
                        className="py-9 px-10 bg-white text-black mouse-hover:bg-black mouse-hover:text-white transition-colors will-change-transform duration-300">
                        <SvgArrowRight width={25} id="section-features" />
                    </button>
                </div>
            </Container>
        </section>
    );
}
